import {
	type CommitInfo,
	type GiteaPermissions,
	getCommits,
	getLatestCommit,
	getTreeFull,
	getUserWorkspace,
	getUserWorkspaces,
	type TreeEntry,
} from "@dopplexr/editor/lib/connectors/gitea";
import {
	cleanupDebounced,
	readFileCached,
	updateFile,
} from "@dopplexr/editor/lib/connectors/opfs";
import type { HistoryItem } from "@dopplexr/editor/lib/history";
import { batchForEach } from "@dopplexr/utils/lib/array";
import { mkdir, readFile, writeFile } from "@zenfs/core/promises";
import { defineStore } from "pinia";

const textEncoder = new TextEncoder();

export type LocalTreeEntry = TreeEntry & {
	local?: "ADDED" | "UPDATED" | "REMOVED";
};

export type Permission = "admin" | "write" | "read";

const formatProjectJSON = (history: HistoryItem[]) => {
	const items = history.map((item) => `${JSON.stringify(item)}`).join("\n\t,");

	return `[\n\t${items}\n]`;
};

export class WorkspaceInfo {
	id: number = 0;
	name: string = "";
	owner: string = "";
	tree: TreeEntry[] = [];
	treeModifications: {
		additions: { [uri: string]: LocalTreeEntry };
		updations: { [uri: string]: LocalTreeEntry };
		deletions: { [uri: string]: LocalTreeEntry };
	} = {
		additions: {},
		updations: {},
		deletions: {},
	};
	path: string = "";
	lastModified: number = Date.now();
	permissions: Permission[] = [];
	lastSynced: string = "";

	constructor(params: {
		id: number;
		name: string;
		owner: string;
		path: string;
		tree?: TreeEntry[];
		treeModifications?: {
			additions: { [uri: string]: LocalTreeEntry };
			updations: { [uri: string]: LocalTreeEntry };
			deletions: { [uri: string]: LocalTreeEntry };
		};
		lastModified?: number;
		lastSynced?: string;
		permissions?: Permission[];
	}) {
		Object.assign(this, params);
	}

	async pull({ branch = "main" } = {}) {
		const commitsCachePath = `cache/commits/${this.owner}/${this.name}`;
		const doppleMetaDataPath = `cache/metadata/${this.owner}/${this.name}`;

		await mkdir(commitsCachePath, { recursive: true });

		const commitSHA = await getLatestCommit({
			owner: this.owner,
			workspace: this.name,
			branch,
		})
			.map(({ id }) => id)
			.unwrapOr(null);

		if (!commitSHA) {
			throw new Error("Invalid Commit");
		}

		const exisingCommitsJSON = await readFile(
			`${commitsCachePath}/commits.json`,
			{ encoding: "utf8" },
		).catch(() => "[]");
		const exisingCommits = JSON.parse(exisingCommitsJSON) as CommitInfo[];

		const commits = await getCommits({
			owner: this.owner,
			workspace: this.name,
			ref: commitSHA,
			lastCommit: exisingCommits[0]?.sha,
		})
			.map((res) => [...res, ...exisingCommits])
			.unwrapOr(exisingCommits);

		const latestLocalCommit = exisingCommits[0]?.sha;

		if (commitSHA === latestLocalCommit) {
			return;
		}

		await getTreeFull({
			owner: this.owner,
			workspace: this.name,
			sha: commitSHA,
		}).map(async (tree) => {
			const oldTree = this.tree;
			this.tree = tree;

			await batchForEach(this.unCommitedTree, async (entry) => {
				const commit = commits.find(({ files }) =>
					files.find(({ filename }) => filename === entry.path),
				);

				if (commit) {
					entry.lastModified = commit.created;
				}

				if (entry.type === "tree") {
					return;
				}

				if (
					entry.path.startsWith("projects") &&
					entry.path.endsWith("history.json")
				) {
					const key = `blob:::${entry.path}`;

					if (
						this.treeModifications.additions[key] ||
						!this.treeModifications.updations[key]
					) {
						// Purely local
						await readFileCached(entry.path, {
							tree: this.unCommitedTree,
							workspacePath: this.path,
							encoding: "utf8",
							ref: commitSHA,
						});

						return;
					}

					const original = await readFileCached(entry.path, {
						tree: oldTree,
						workspacePath: this.path,
						encoding: "utf8",
						ref: commitSHA,
					})
						.then((res) => JSON.parse(res) as HistoryItem[])
						.catch(() => null);

					if (!original) {
						await readFileCached(entry.path, {
							tree: this.unCommitedTree,
							workspacePath: this.path,
							encoding: "utf8",
							ref: commitSHA,
						});
						return;
					}

					const latest = await readFileCached(entry.path, {
						tree,
						workspacePath: this.path,
						encoding: "utf8",
						ref: commitSHA,
					}).then((res) => JSON.parse(res) as HistoryItem[]);

					const localCopy = await readFileCached(entry.path, {
						tree: this.unCommitedTree,
						workspacePath: this.path,
						encoding: "utf8",
						ref: commitSHA,
					}).then((res) => JSON.parse(res) as HistoryItem[]);

					// TODO: If items were dropped during Conflict Resolution, original.length needs to reduced by that amount.
					localCopy.splice(
						original.length,
						0,
						...latest.slice(original.length),
					);

					await updateFile(
						entry.path,
						textEncoder.encode(formatProjectJSON(localCopy)).buffer,
						this,
					);

					return;
				}

				await readFileCached(entry.path, {
					tree,
					workspacePath: this.path,
					offline: true,
				});
			});

			console.debug("writing... ");

			await writeFile(`${doppleMetaDataPath}/meta.json`, JSON.stringify(this));
		});

		await writeFile(
			`${commitsCachePath}/commits.json`,
			JSON.stringify(commits),
		);

		cleanupDebounced();

		this.lastModified = Date.now();
	}

	get unCommitedTree() {
		const wi = this as WorkspaceInfo;

		const fullTree: { [uri: string]: LocalTreeEntry } = {};

		for (const entry of wi.tree) {
			fullTree[`${entry.type}:::${entry.path}`] = entry;
		}

		for (const [key, entry] of Object.entries(wi.treeModifications.additions)) {
			const existingEntry = fullTree[key];

			if (!existingEntry) {
				fullTree[key] = entry;
				continue;
			}

			if (existingEntry.sha === entry.sha) {
				delete wi.treeModifications.additions[key];
				continue;
			}

			delete wi.treeModifications.additions[key];
			const updateEntry: LocalTreeEntry = { ...entry, local: "UPDATED" };
			wi.treeModifications.updations[key] = updateEntry;
			fullTree[key] = updateEntry;
		}

		for (const [uri, entry] of Object.entries(wi.treeModifications.updations)) {
			const existingEntry = fullTree[uri];

			if (!existingEntry) {
				delete wi.treeModifications.updations[uri];
				const addEntry: LocalTreeEntry = { ...entry, local: "ADDED" };
				wi.treeModifications.additions[uri] = addEntry;
				fullTree[uri] = addEntry;

				continue;
			}

			if (existingEntry.sha === entry.sha) {
				delete wi.treeModifications.updations[uri];
				continue;
			}

			fullTree[uri] = entry;
		}

		for (const [uri, entry] of Object.entries(wi.treeModifications.deletions)) {
			let found = false;

			for (const [treeUri, { path }] of Object.entries(fullTree)) {
				if (path.startsWith(entry.path)) {
					delete fullTree[treeUri];

					found = true;
				}
			}

			if (!found) {
				delete wi.treeModifications.deletions[uri];
			}
		}

		return Object.values(fullTree);
	}
}

export const convertPermissions = (gitea: GiteaPermissions): Permission[] => {
	return [
		gitea.admin && "admin",
		gitea.push && "write",
		gitea.pull && "read",
	].filter(Boolean) as Permission[];
};

export const useWorkspacesStore = defineStore("workspaces", {
	state: () => ({
		workspaces: [] as WorkspaceInfo[],
		fetched: false,
		loading: false,
	}),
	getters: {
		workspace(_state) {
			return ({ name, owner }: { name: string; owner: string }) => {
				const workspaceInfo = this.workspaces.find(
					(ws) => ws.owner === owner && ws.name === name,
				);

				return workspaceInfo;
			};
		},
	},
	actions: {
		async fetchWorkspaces() {
			this.loading = true;

			const workspaces = await getUserWorkspaces().map((res) => {
				return res.map((ws) => {
					const workspacePath = `repos/${ws.owner.login}/${ws.name}`;
					return new WorkspaceInfo({
						id: ws.id,
						name: ws.name,
						owner: ws.owner.login,
						path: workspacePath,
						lastSynced: ws.updated_at,
					});
				});
			});

			if (workspaces.isOk()) {
				this.workspaces = workspaces.value;
			}

			this.fetched = true;
			this.loading = false;
		},
		async fetchWorkspace(owner: string, name: string) {
			const workspaceInfo = this.workspaces.find(
				(ws) => ws.owner === owner && ws.name === name,
			);

			if (!workspaceInfo) {
				return null;
			}

			const doppleMetaDataPath = `cache/metadata/${owner}/${name}`;

			const workspaceRes = await getUserWorkspace({
				owner,
				workspace: name,
			});

			if (workspaceRes.isErr()) {
				return;
			}

			await readFile(`${doppleMetaDataPath}/meta.json`, { encoding: "utf8" })
				.then(JSON.parse)
				.catch(() => {})
				.then(({ treeModifications, tree, lastModified } = {}) => {
					if (treeModifications) {
						workspaceInfo.treeModifications = treeModifications;
					}

					if (tree) {
						workspaceInfo.tree = tree;
					}

					if (lastModified) {
						workspaceInfo.lastModified = lastModified;
					}
				});

			workspaceInfo.permissions = convertPermissions(
				workspaceRes.value.permissions,
			);

			await mkdir(doppleMetaDataPath, { recursive: true });
		},
	},
});
